<template>
  <div class="bt one-whole pl-small flex flex--column h-full">
    <ak-loading
      v-model:active="isLoading"
      :can-cancel="false"
      :is-full-page="false"
      :backdrop="false"
    />
    <slot name="searchFilter" />
    <div style="overflow-y: auto" class="pr-tiny pb-huge">
      <ul class="list-bare mt-small">
        <li
          class="campaign-list-item"
          :class="{ 'is-active': isActive(null) }"
          @click.stop.prevent="setProjectFilter(null)"
        >
          All containers
        </li>
      </ul>
      <ak-drag-and-drop-tree
        v-if="!isLoading"
        v-model:data="treeData"
        show-empty
        :is-tree="isFolder"
        :is-open="isFolderOpen"
        :extra-item-class="itemClasses"
        :max-tree-depth="1"
        :max-item-depth="2"
        @move="move"
        @tree-opened="folderOpened"
        @tree-closed="folderClosed"
      >
        <template #default="{ item, open, isTree, children }">
          <folder-item
            v-if="isTree"
            :item="item"
            :children="children"
            :is-open="open"
            @click.stop.prevent="folderFilter(item._id, children)"
          />
          <project-item
            v-else
            :item="item"
            @click.stop.prevent="setProjectFilter(item._id)"
          />
        </template>
        <template #empty>Empty Folder</template>
      </ak-drag-and-drop-tree>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch, inject } from 'vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import DragAndDropTree from '@akerolabs/akero-vue/src/components/tree/DragAndDropTree.vue'
import FolderItem from '../FolderItem.vue'
import ProjectItem from '../ProjectItem.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { userLevelIsHigerThan } from '@akerolabs/akero-vue/src/services/users.js'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
export default defineComponent({
  name: 'FoldersTab',
  components: {
    [Loading.name]: Loading,
    [DragAndDropTree.name]: DragAndDropTree,
    [FolderItem.name]: FolderItem,
    [ProjectItem.name]: ProjectItem,
  },
  props: {
    tree: {
      type: Array,
      default: () => [],
    },
    list: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:tree'],
  setup(props) {
    const treeData = ref(props.tree)
    const isLoading = ref(false)
    const updateData = inject('updateData')
    const setProjectFilter = inject('setProjectFilter')
    const folderOpened = inject('folderOpened')
    const folderClosed = inject('folderClosed')
    const isFolderOpen = inject('isFolderOpen')
    const setFolderFilter = inject('setFolderFilter')
    const isActive = inject('isActive')

    watch(
      () => props.tree,
      (value) => {
        treeData.value = value
      },
      { deep: true },
    )

    const isFolder = (item) => {
      return item.isFolder === true
    }

    const move = async ({ item, target, dropItem }) => {
      isLoading.value = true
      const parent =
        target.data && target.data.parent ? target.data.parent : null
      const targetPosition = dropItem ? dropItem.position : 0
      if (userLevelIsHigerThan('agencyuser')) {
        const { data } = await request.get(`/users/${item.data.createdBy}`)
        if (['accountuser', 'accountadmin'].includes(data.data.type)) {
          DialogProgrammatic.confirm({
            title: 'Moving Campaign',
            message: `
              <p>
                This campaign was created by the client, please make sure they are happy to have this campaign moved.
              </p>
              <p>
                Are you sure you wish to move this campaign?
              </p>
            `,
            confirmText: 'Okay',
            type: 'success',
            size: 'big',
            onConfirm: async () => {
              await updatePositions(item.data, parent, targetPosition)
              await updateData()
              isLoading.value = false
            },
            onCancel: async () => {
              await updateData()
              isLoading.value = false
            },
          })
        } else {
          await updatePositions(item.data, parent, targetPosition)
          await updateData()
          isLoading.value = false
        }
      } else {
        await updatePositions(item.data, parent, targetPosition)
        await updateData()
        isLoading.value = false
      }
    }

    const updatePositions = async (item, parent, index) => {
      await request.post('/campaigns/position', {
        _id: item._id,
        account: item.account,
        agency: item.agency,
        position: index,
        parent,
      })
    }

    const itemClasses = ({ archived }) => {
      const classes = {}
      if (archived) {
        classes['is-archived'] = true
      }
      return classes
    }

    const getChildren = (children) => {
      let array = []
      children.forEach((child) => {
        if (child.data && child.data._id) {
          array.push(child.data._id)
        }
        if (child.children && child.children.length > 0) {
          array = [...array, ...getChildren(child.children)]
        }
      })
      return array
    }

    const folderFilter = (id, children) => {
      setFolderFilter(id, getChildren(children))
    }

    return {
      isLoading,
      treeData,
      isFolder,
      move,
      folderOpened,
      folderClosed,
      isFolderOpen,
      itemClasses,
      setProjectFilter,
      folderFilter,
      isActive,
    }
  },
})
</script>
